import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, AlertTriangle, ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { apiGet, apiPost, formatDateTime } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { SymbolLink } from "@/components/common/StockLinks";

const POLL_INTERVAL_MS = 60_000;

const stripBullet = (s) => (s || "").replace(/^[\s✅⚠•·\-–—]+/, "").trim();

/** Format a relative time string ("2m ago", "3h ago", "yesterday"). */
const relTime = (iso) => {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  if (diff < 604_800_000) return `${Math.floor(diff / 86_400_000)}d ago`;
  return formatDateTime(iso);
};

/**
 * Notification bell — Phase 6 Fix 3 makes it market-aware:
 *   • CSE alert → 🇱🇰 flag + SymbolLink to /stocks/{company_code}
 *   • US alert  → 🇺🇸 flag + link to /us-stocks/{us_symbol}
 *
 * The two markets share the same dropdown but each row is tagged so
 * users can see at a glance which market triggered the alert. Strict
 * separation: we never combine fields across markets in the same row.
 */

const flagFor = (market) => (market === "US" ? "🇺🇸" : "🇱🇰");

const NotificationBell = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread_count: 0 });

  const load = useCallback(() => {
    // Phase 6 Fix Pack 3 — guard against the race where the bell
    // mounts before the auth interceptor is installed in <AuthProvider>.
    // Without this guard the very first apiGet hits the API without an
    // Authorization header, returns 401, the .catch swallows the error,
    // and the bell sits on its initial empty state until the next 60s
    // poll — which is exactly the "No alerts" symptom seen on first
    // page load even when the DB has live US alerts.
    if (authLoading || !user) return;
    apiGet("/alerts/notifications?limit=8").then(setData).catch(() => {});
  }, [user, authLoading]);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_INTERVAL_MS);
    const onAlert = () => load();
    window.addEventListener("cse:alerts-updated", onAlert);
    return () => {
      clearInterval(id);
      window.removeEventListener("cse:alerts-updated", onAlert);
    };
  }, [load]);

  const handleOpenChange = async (next) => {
    setOpen(next);
    if (next && data.unread_count > 0) {
      try {
        await apiPost("/alerts/notifications/mark-read");
        setData((d) => ({ ...d, unread_count: 0 }));
      } catch {
        /* noop */
      }
    }
  };

  const items = data.items || [];
  const unread = data.unread_count || 0;

  const onViewItem = (it) => {
    setOpen(false);
    if ((it.market || "CSE").toUpperCase() === "US") {
      const sym = it.us_symbol;
      if (!sym) return;
      navigate(`/us-stocks/${encodeURIComponent(sym)}`, {
        state: { from: { pathname: "/alerts", label: "Alerts" } },
      });
    } else {
      navigate(`/stocks/${it.company_code}`, {
        state: { from: { pathname: "/alerts", label: "Alerts" } },
      });
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Notifications"
          data-testid="notifications-bell"
          className="relative"
        >
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span
              data-testid="notifications-bell-badge"
              className="absolute -top-0.5 -right-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[hsl(var(--destructive))] px-1 text-[10px] font-semibold text-white tabular"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[360px] p-0 overflow-hidden"
        data-testid="notifications-dropdown"
      >
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-[hsl(var(--warning))]" />
            <span className="font-display text-sm font-semibold">Triggered alerts</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => {
              setOpen(false);
              navigate("/alerts");
            }}
            data-testid="notifications-view-all"
          >
            View all <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </div>
        {items.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground">
            No alerts have triggered yet.
          </div>
        ) : (
          <ul className="divide-y divide-border max-h-[420px] overflow-y-auto">
            {items.map((it) => {
              const market = (it.market || "CSE").toUpperCase();
              const isUs = market === "US";
              const symbolDisplay = isUs ? it.us_symbol : it.cse_symbol;
              return (
                <li
                  key={it.alert_id}
                  className="px-4 py-3 hover:bg-muted/40"
                  data-testid={`notification-item-${it.alert_id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div
                      className="flex items-center gap-1.5 min-w-0"
                      data-testid={`notification-symbol-${it.alert_id}`}
                    >
                      <span
                        className="text-base leading-none shrink-0"
                        title={isUs ? "US market" : "CSE market"}
                        data-testid={`notification-flag-${it.alert_id}`}
                      >
                        {flagFor(market)}
                      </span>
                      {isUs ? (
                        <button
                          type="button"
                          onClick={() => onViewItem(it)}
                          className="text-sm font-semibold text-[hsl(var(--primary))] hover:underline truncate"
                        >
                          {symbolDisplay}
                          {it.company_name && (
                            <span className="ml-1.5 text-xs font-normal text-muted-foreground truncate">
                              · {it.company_name}
                            </span>
                          )}
                        </button>
                      ) : (
                        <SymbolLink
                          companyCode={it.company_code}
                          symbol={it.cse_symbol}
                          fromLabel="Alerts"
                          className="text-sm"
                        />
                      )}
                    </div>
                    <span className="text-[10px] text-muted-foreground tabular shrink-0">
                      {relTime(it.triggered_at)}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {it.trigger_message || it.alert_name}
                  </p>
                  {(it.rationale_pro || it.rationale_con) && !isUs && (
                    <div
                      className="mt-1.5 space-y-1"
                      data-testid={`notification-rationale-${it.alert_id}`}
                    >
                      {it.rationale_pro && (
                        <div className="flex gap-1.5 text-[11px] leading-snug">
                          <CheckCircle2 className="h-3 w-3 mt-0.5 shrink-0 text-[hsl(var(--success))]" />
                          <span className="text-foreground/85">
                            {stripBullet(it.rationale_pro)}
                          </span>
                        </div>
                      )}
                      {it.rationale_con && (
                        <div className="flex gap-1.5 text-[11px] leading-snug">
                          <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0 text-[hsl(var(--warning))]" />
                          <span className="text-foreground/85">
                            {stripBullet(it.rationale_con)}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => onViewItem(it)}
                    className="mt-1.5 text-xs text-[hsl(var(--primary))] hover:underline"
                    data-testid={`notification-view-${it.alert_id}`}
                  >
                    View stock →
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
